import { useMemo } from 'react'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import useEarningsOverview from './useEarningsOverview'
import { formatMoneyValue } from '../formatter'
import { PERIOD_OPTIONS } from '../Static/constants'

function getPeriodLabel(scope) {
  const option = PERIOD_OPTIONS.find((opt) => opt.value === scope)
  if (!option) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`useOverviewKPI: unknown earningsScope "${scope}" - missing from PERIOD_OPTIONS`)
    }
    return ''
  }
  return option.label
}

function isToday(dateString) {
  if (!dateString) return false

  const today = new Date()
  const date = new Date(dateString)

  return (
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  )
}

function useOverviewKPI() {
  const earningsOverview = useEarningsOverview(12)

  const { data, earningsScope, isDataFetched } = earningsOverview

  const subscriptionChartData = useMemo(
    () => data.subscription_earnings?.chart_data || EMPTY_OBJECT,
    [data]
  )

  const subscriptionDownloadCount = data.subscription_earnings?.download_count || 0

  const tiles = useMemo(() => {
    if (!isDataFetched) return null

    const regularPeriodTotal = data.earnings?.total_by_period || 0
    const subscriptionPeriodTotal = data.subscription_earnings?.total_by_period || 0
    const totalEarnings = regularPeriodTotal + subscriptionPeriodTotal
    const lastSalePrice = data.earnings?.last_sale?.price || 0
    const todayDelta = isToday(data.earnings?.last_sale?.created_at) ? lastSalePrice : 0
    // *ThisMonth values are consumed by RegularEarningsDetailPanel / SubscriptionDetailPanel,
    // whose headers render "$X this month" independently of the period selector.
    const regularThisMonth = data.earnings?.this_month || 0
    const subscriptionThisMonth = data.subscription_earnings?.this_month || 0

    return {
      totalEarnings,
      todayDelta,
      regularPeriodTotal,
      subscriptionPeriodTotal,
      regularThisMonth,
      subscriptionThisMonth,
      periodLabel: getPeriodLabel(earningsScope),
    }
  }, [isDataFetched, data, earningsScope])

  const payoutRate = useMemo(() => {
    if (!isDataFetched) return null

    const rate = data.royalty_rate || 0
    const isCustom = Boolean(data.custom_royalty_rate)
    const annualRevenue = data.annual_revenue || 0
    const nextThreshold = data.next_level_gmv_threshold || 0
    const amountToNext = Number(formatMoneyValue(Math.max(0, nextThreshold - annualRevenue)))
    const nextRate = data.next_level_royalty_rate || 0
    const levelId = data.level_id || 0

    return { rate, isCustom, amountToNext, nextRate, levelId }
  }, [isDataFetched, data])

  return {
    tiles,
    payoutRate,
    subscriptionChartData,
    subscriptionDownloadCount,
    earningsOverview,
    isLoading: !isDataFetched,
  }
}

export default useOverviewKPI
